import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import React, { useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import TileCard from 'tripPlanner/components/EntryPoints/Common/Tile/TileCard'
import { ThemeItem } from 'tripPlanner/hooks/api/tripGenerationData'
import TileCardsGroup from './Common/Tile/TileCardsGroup'
import * as Analytics from 'analytics/analytics'

const LoadingContainer = styled(VerticalSpacer)`
  padding-block: ${rem(24)};
`

interface Props {
  themes: Array<ThemeItem> | undefined
  themesLoading: boolean
  selectedThemeNames: Array<string>
  onThemeToggle: (theme: string) => void
  isGeneratingTrip: boolean
  stepNumber: number
  totalSteps: number
}

function AITripThemeSelection(props: Props) {
  const {
    themes,
    themesLoading,
    selectedThemeNames,
    onThemeToggle,
    isGeneratingTrip,
    stepNumber,
    totalSteps,
  } = props
  const intl = useIntl()
  const selectedThemeSet = useMemo(() =>
    new Set(selectedThemeNames),
  [selectedThemeNames])

  const fallBackThemes: Array<ThemeItem> = [
    {
      theme: intl.formatMessage({
        id: 'qp7LLOWU/b',
        defaultMessage: 'Tourist highlights',
        description: 'Theme option for tourist highlights',
      }),
      description: '⭐',
    },
    {
      theme: intl.formatMessage({
        id: 'pQjcTrU/ua',
        defaultMessage: 'Beach days',
        description: 'Theme option for beach days',
      }),
      description: '🏖️',
    },
    {
      theme: intl.formatMessage({
        id: 'Yz2jkXLH7U',
        defaultMessage: 'Coastal walks',
        description: 'Theme option for coastal walks',
      }),
      description: '🌊',
    },
    {
      theme: intl.formatMessage({
        id: 'j5xeK2fUk2',
        defaultMessage: 'Harbour cruises',
        description: 'Theme option for harbour cruises',
      }),
      description: '🚢',
    },
    {
      theme: intl.formatMessage({
        id: 'eC4TB9Swr8',
        defaultMessage: 'Local food & wine',
        description: 'Theme option for local food and wine',
      }),
      description: '🍷',
    },
    {
      theme: intl.formatMessage({
        id: '65hORAgouo',
        defaultMessage: 'Outdoor adventure',
        description: 'Theme option for outdoor adventure',
      }),
      description: '🥾',
    },
    {
      theme: intl.formatMessage({
        id: 'zLL0N6oydu',
        defaultMessage: 'Arts & Museums',
        description: 'Theme option for arts and museums',
      }),
      description: '🖼️',
    },
    {
      theme: intl.formatMessage({
        id: 'AHz4r11186',
        defaultMessage: 'Neighbourhood exploring',
        description: 'Theme option for neighbourhood exploring',
      }),
      description: '🗺️',
    },
    {
      theme: intl.formatMessage({
        id: 'LdqK9u5k0g',
        defaultMessage: 'Shopping',
        description: 'Theme option for shopping',
      }),
      description: '🛍️',
    },
  ]

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'themes',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  const stepText = intl.formatMessage(
    {
      id: 'ewX/UMLdx4',
      defaultMessage: '{stepNumber} of {totalSteps}',
      description: 'Step indicator showing current step number and total steps',
    },
    { stepNumber, totalSteps },
  )

  const headingText = intl.formatMessage(
    {
      id: 'zcdAq74l83',
      defaultMessage: 'Trip <i>Vibes</i>',
      description: 'Heading text with italicized Vibes',
    },
    {
      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
    },
  )

  const descriptionText = intl.formatMessage({
    id: 'tMppoVJCNF',
    defaultMessage: "Pick the experiences that excite you! Let's set the vibe just right for you.",
    description: 'Description text encouraging users to select trip themes',
  })

  return (
    <Group direction="vertical" gap={20} desktopGap={24}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large" weight="bold">{stepText}</BodyText>
        <Heading variant="heading1">
          {headingText}
        </Heading>
        <BodyText variant="large">
          {descriptionText}
        </BodyText>
      </VerticalSpacer>

      {themesLoading && (
        <LoadingContainer gap={24}>
          <VerticalSpacer gap={4}>
            <TextLoadingBox width={144} lines={1} typography="body-large" />
            <TextLoadingBox width={360} lines={2} typography="body-medium" />
          </VerticalSpacer>
          <VerticalSpacer gap={4}>
            <TextLoadingBox width={144} lines={1} typography="body-large" />
            <TextLoadingBox width={360} lines={2} typography="body-medium" />
          </VerticalSpacer>
          <VerticalSpacer gap={4}>
            <TextLoadingBox width={144} lines={1} typography="body-large" />
            <TextLoadingBox width={360} lines={2} typography="body-medium" />
          </VerticalSpacer>
        </LoadingContainer>
      )}

      {!themesLoading && (
        <TileCardsGroup>
          {(themes ?? fallBackThemes).map((item) => (
            <TileCard
              key={item.theme}
              title={item.theme}
              icon={item.description}
              value={item.theme}
              onClick={onThemeToggle}
              selected={selectedThemeSet.has(item.theme)}
              disabled={isGeneratingTrip}
            />
          ))}
        </TileCardsGroup>
      )}
    </Group>
  )
}

export default AITripThemeSelection
